// Mail-Entwürfe, Antworten, Verlauf und Dateien (Runde 6, Agent 6B): Typen und Client nach crm/API.md, Abschnitt 21.3. Vorarbeit:
// die Formen stehen, die Routen antworten bis zum Bau von 6B mit leeren Listen bzw. 501 `nicht_umgesetzt`. 6B erweitert diese Datei
// nur additiv. Die bestehenden Wege (`/api/kommunikation/…`) bleiben in `kommunikation/api.ts`.
import { http } from "./client";
import type { Aktivitaet } from "./types";
import type { MailAdresseDaten } from "./mailEingang";

export type EntwurfStatus = "entwurf" | "geplant" | "sendet" | "gesendet" | "fehler" | "abgebrochen";
export type EntwurfArt = "neu" | "antwort" | "allen" | "weiterleiten";
export type DateiBesitzerArt = "nachricht" | "vorlage" | "signatur" | "entwurf" | "massenmail";

/** Feste Unterlage auf dem Server (`/opt/fb-automatik/unterlagen`) */
export interface Unterlage {
  name: string;
  titel: string;
  /** Name, unter dem der Empfänger die Datei sieht */
  dateiname: string;
  groesse: number;
}

export interface MailDatei {
  id: string;
  besitzer_art: DateiBesitzerArt;
  besitzer_id: string;
  dateiname: string;
  content_type: string | null;
  groesse: number;
  inline_cid: string | null;
  /** Adresse zum Herunterladen */
  url: string;
}

export interface MailEntwurf {
  id: string;
  user_id: string;
  /** Bezug beim Antworten (Message-ID der Originalmail) */
  in_reply_to?: string | null;
  referenzen?: string | null;
  activity_id?: string | null;
  date_created?: string | null;
  lead_id: string | null;
  contact_id: string | null;
  art: EntwurfArt;
  bezug_activity_id: string | null;
  an: MailAdresseDaten[];
  cc: MailAdresseDaten[];
  bcc: MailAdresseDaten[];
  betreff: string | null;
  body_html: string | null;
  vorlage_id: string | null;
  dateien: MailDatei[];
  status: EntwurfStatus;
  geplant_at: string | null;
  gesendet_at: string | null;
  fehler: string | null;
  quelle: "nutzer" | "workflow";
  date_updated: string | null;
}

export interface EntwurfEingabe {
  lead_id?: string;
  contact_id?: string;
  art?: EntwurfArt;
  bezug_activity_id?: string;
  an?: MailAdresseDaten[];
  cc?: MailAdresseDaten[];
  bcc?: MailAdresseDaten[];
  betreff?: string;
  body_html?: string;
  vorlage_id?: string;
  datei_ids?: string[];
}

/** Antwort von `POST /api/mail/antwort-vorbereiten`: Felder für den Komposer, noch nichts gespeichert */
export interface AntwortVorlage {
  art: Exclude<EntwurfArt, "neu">;
  bezug_activity_id: string;
  lead_id?: string | null;
  contact_id?: string | null;
  /** Anhänge der Originalmail, die nur in Close liegen (Weiterleiten holt sie beim Anlegen des Entwurfs) */
  close_anhaenge?: { nr: number; dateiname: string; groesse: number | null; content_type: string | null }[];
  hinweise?: string[];
  an: MailAdresseDaten[];
  cc: MailAdresseDaten[];
  betreff: string;
  body_html: string;
  in_reply_to: string | null;
  referenzen: string | null;
  /** nur beim Weiterleiten */
  dateien: MailDatei[];
}

/** Ergebnis von `POST /api/mail/entwuerfe/{id}/senden` */
export interface EntwurfGesendet {
  entwurf: MailEntwurf | null;
  activity_id: string | null;
  activity?: Aktivitaet;
  email?: { message_id: string; von: string; an: string[]; cc: string[]; bcc: string[]; abgelegt: boolean; ablage_fehler: string | null; abgelehnt: string[]; thread_key: string };
  close?: { geloggt: boolean; fehler: string | null };
}

export interface MailVerlaufNachricht {
  activity_id: string;
  richtung: "eingehend" | "ausgehend";
  von: MailAdresseDaten;
  an: MailAdresseDaten[];
  cc: MailAdresseDaten[];
  betreff: string | null;
  datum: string | null;
  text: string | null;
  html: string | null;
  dateien: MailDatei[];
}

const k = encodeURIComponent;

export const mailEntwuerfeApi = {
  liste: (p: { lead_id?: string; status?: EntwurfStatus[]; limit?: number } = {}) =>
    http.get<{ data: MailEntwurf[] }>("/api/mail/entwuerfe", { lead_id: p.lead_id, status: p.status, limit: p.limit }),
  anlegen: (e: EntwurfEingabe) => http.post<{ entwurf: MailEntwurf; hinweise?: string[] }>("/api/mail/entwuerfe", e),
  eine: (id: string) => http.get<{ entwurf: MailEntwurf }>(`/api/mail/entwuerfe/${k(id)}`),
  aendern: (id: string, e: Omit<EntwurfEingabe, "lead_id" | "contact_id" | "art" | "bezug_activity_id">) =>
    http.patch<{ entwurf: MailEntwurf }>(`/api/mail/entwuerfe/${k(id)}`, e),
  loeschen: (id: string) => http.del<void>(`/api/mail/entwuerfe/${k(id)}`),
  planen: (id: string, geplantAt: string, bestaetigt: string[] = []) =>
    http.post<{ entwurf: MailEntwurf; versand_freigegeben: boolean }>(`/api/mail/entwuerfe/${k(id)}/planen`, {
      geplant_at: geplantAt,
      bestaetigt,
    }),
  planAufheben: (id: string) => http.post<{ entwurf: MailEntwurf }>(`/api/mail/entwuerfe/${k(id)}/plan-aufheben`),
  senden: (id: string, p: { bestaetigt?: string[]; anfrage_id?: string } = {}) =>
    http.post<EntwurfGesendet>(`/api/mail/entwuerfe/${k(id)}/senden`, p),
  antwortVorbereiten: (activityId: string, art: "antwort" | "allen" | "weiterleiten") =>
    http.post<{ vorlage: AntwortVorlage }>("/api/mail/antwort-vorbereiten", { activity_id: activityId, art }),
  verlauf: (threadKey: string, leadId?: string) =>
    http.get<{ thread_key: string; lead_id?: string; nachrichten: MailVerlaufNachricht[] }>(`/api/mail/verlauf/${k(threadKey)}`, { lead_id: leadId }),
  dateiHochladen: (e: { besitzer_art: "vorlage" | "signatur" | "entwurf" | "massenmail"; besitzer_id: string; dateiname: string; content_type?: string; inhalt_base64: string; inline_cid?: string }) =>
    http.post<{ datei: MailDatei }>("/api/mail/dateien", e),
  /** Feste Unterlagen auf dem Server (Weiterbildungsübersicht, Präsentation, …) */
  unterlagen: () => http.get<{ data: Unterlage[] }>("/api/mail/unterlagen"),
  /** Datei aus den Lead-Dateien oder den Unterlagen als Anhang übernehmen (Entwurf, Vorlage oder Massen-Mail) */
  uebernehmen: (e: { besitzer_art: "vorlage" | "entwurf" | "massenmail"; besitzer_id: string; quelle: "lead" | "unterlagen"; lead_id?: string; datei_id?: string; name?: string }) =>
    http.post<{ datei: MailDatei }>("/api/mail/dateien/uebernehmen", e),
  dateiUrl: (id: string) => `/api/mail/dateien/${k(id)}`,
  dateiLoeschen: (id: string) => http.del<void>(`/api/mail/dateien/${k(id)}`),
};
